// Global Instructions Rule Applied!
//
// Configuration → Deal Documents services: the aggregate admin read plus CRUD
// over cfg.document_categories / document_types / deal_document_requirements.
// Same fetch conventions as dealExecutionConfigService.
//
// Every route here is admin-gated at the backend (403 for non-admins), so a
// 403 surfaces as the readable "Administrator access required" from the body.
import { API_ROUTES, buildFullUrl, getBackendBaseUrl } from '../../../core/utils/endpoints.ts'
import {
  DocumentCategoryAdminRow,
  DocumentConfigAdminData,
  DocumentRequirementAdminRow,
  DocumentTypeAdminRow,
  validateDocumentCategoryAdminRow,
  validateDocumentRequirementAdminRow,
  validateDocumentTypeAdminRow
} from '../../../state_manager/data_models/document_config_model.ts'

const BACKEND_BASE_URL = getBackendBaseUrl()

function authHeaders(accessToken: string | null): Record<string, string> {
  const headers: Record<string, string> = { 'Content-Type': 'application/json' }
  if (accessToken) headers['Authorization'] = `Bearer ${accessToken}`
  return headers
}

// Writes use the house request envelope { data: {...} } — the /config/* routes
// unwrap it via _json_body, same as pipelinesConfigService.
async function readJson(response: Response): Promise<any> {
  const body = await response.json().catch(() => ({}))
  if (!response.ok) {
    // The backend's error envelope key is `message` (handle_route_custom_error /
    // handle_route_error); `error` is the auth guard's. Reading `message` is what
    // makes the 403 above surface readably. Same 401 wording as
    // pipelinesConfigService so every Configuration panel reads alike.
    if (response.status === 401) throw new Error('Authentication failed - please log in again')
    throw new Error(body.message || body.error || `Request failed (${response.status})`)
  }
  return body
}

export const fetchDocumentConfig = async (
  accessToken: string | null
): Promise<DocumentConfigAdminData> => {
  const response = await fetch(
    buildFullUrl(BACKEND_BASE_URL, API_ROUTES.CONFIG.DOCUMENT_CONFIG_LIST),
    { method: API_ROUTES.CONFIG.DOCUMENT_CONFIG_LIST.method, headers: authHeaders(accessToken) }
  )
  const body = await readJson(response)
  const data = (body.data || {}) as DocumentConfigAdminData
  const categories = (data.categories || []) as DocumentCategoryAdminRow[]
  const types = (data.types || []) as DocumentTypeAdminRow[]
  const requirements = (data.requirements || []) as DocumentRequirementAdminRow[]
  validateDocumentCategoryAdminRow(categories, { isArray: true })
  validateDocumentTypeAdminRow(types, { isArray: true })
  validateDocumentRequirementAdminRow(requirements, { isArray: true })
  return { categories, types, requirements }
}

// -- categories --------------------------------------------------------------
export interface DocumentCategoryFields {
  label?: string
  sort_order?: number | null
  active?: boolean
}

export const createDocumentCategory = async (
  accessToken: string | null,
  fields: DocumentCategoryFields
): Promise<void> => {
  const response = await fetch(
    buildFullUrl(BACKEND_BASE_URL, API_ROUTES.CONFIG.DOCUMENT_CATEGORY_CREATE),
    {
      method: API_ROUTES.CONFIG.DOCUMENT_CATEGORY_CREATE.method,
      headers: authHeaders(accessToken),
      body: JSON.stringify({ data: fields })
    }
  )
  await readJson(response)
}

export const updateDocumentCategory = async (
  accessToken: string | null,
  categoryId: string,
  fields: DocumentCategoryFields
): Promise<void> => {
  const response = await fetch(
    buildFullUrl(BACKEND_BASE_URL, API_ROUTES.CONFIG.DOCUMENT_CATEGORY_UPDATE, { categoryId }),
    {
      method: API_ROUTES.CONFIG.DOCUMENT_CATEGORY_UPDATE.method,
      headers: authHeaders(accessToken),
      body: JSON.stringify({ data: fields })
    }
  )
  await readJson(response)
}

export const deleteDocumentCategory = async (
  accessToken: string | null,
  categoryId: string
): Promise<void> => {
  const response = await fetch(
    buildFullUrl(BACKEND_BASE_URL, API_ROUTES.CONFIG.DOCUMENT_CATEGORY_DELETE, { categoryId }),
    { method: API_ROUTES.CONFIG.DOCUMENT_CATEGORY_DELETE.method, headers: authHeaders(accessToken) }
  )
  await readJson(response)
}

// -- document types ----------------------------------------------------------
export interface DocumentTypeFields {
  label?: string
  sort_order?: number | null
  active?: boolean
  document_category_id?: string | null
}

export const createDocumentType = async (
  accessToken: string | null,
  fields: DocumentTypeFields
): Promise<void> => {
  const response = await fetch(
    buildFullUrl(BACKEND_BASE_URL, API_ROUTES.CONFIG.DOCUMENT_TYPE_CREATE),
    {
      method: API_ROUTES.CONFIG.DOCUMENT_TYPE_CREATE.method,
      headers: authHeaders(accessToken),
      body: JSON.stringify({ data: fields })
    }
  )
  await readJson(response)
}

export const updateDocumentType = async (
  accessToken: string | null,
  typeId: string,
  fields: DocumentTypeFields
): Promise<void> => {
  const response = await fetch(
    buildFullUrl(BACKEND_BASE_URL, API_ROUTES.CONFIG.DOCUMENT_TYPE_UPDATE, { typeId }),
    {
      method: API_ROUTES.CONFIG.DOCUMENT_TYPE_UPDATE.method,
      headers: authHeaders(accessToken),
      body: JSON.stringify({ data: fields })
    }
  )
  await readJson(response)
}

export const deleteDocumentType = async (
  accessToken: string | null,
  typeId: string
): Promise<void> => {
  const response = await fetch(
    buildFullUrl(BACKEND_BASE_URL, API_ROUTES.CONFIG.DOCUMENT_TYPE_DELETE, { typeId }),
    { method: API_ROUTES.CONFIG.DOCUMENT_TYPE_DELETE.method, headers: authHeaders(accessToken) }
  )
  await readJson(response)
}

// -- requirements ------------------------------------------------------------
export interface DocumentRequirementFields {
  name?: string
  /** Each scope is a list of ids; [] = applies to all. */
  pipeline_ids?: string[]
  deal_type_ids?: string[]
  security_type_ids?: string[]
  fund_ids?: string[]
  required_lifecycle?: string
  satisfied_by_document_type_ids?: string[]
  sequence?: number | null
  active?: boolean
}

export const createDocumentRequirement = async (
  accessToken: string | null,
  fields: DocumentRequirementFields
): Promise<void> => {
  const response = await fetch(
    buildFullUrl(BACKEND_BASE_URL, API_ROUTES.CONFIG.DOCUMENT_REQUIREMENT_CREATE),
    {
      method: API_ROUTES.CONFIG.DOCUMENT_REQUIREMENT_CREATE.method,
      headers: authHeaders(accessToken),
      body: JSON.stringify({ data: fields })
    }
  )
  await readJson(response)
}

export const updateDocumentRequirement = async (
  accessToken: string | null,
  requirementId: string,
  fields: DocumentRequirementFields
): Promise<void> => {
  const response = await fetch(
    buildFullUrl(BACKEND_BASE_URL, API_ROUTES.CONFIG.DOCUMENT_REQUIREMENT_UPDATE, { requirementId }),
    {
      method: API_ROUTES.CONFIG.DOCUMENT_REQUIREMENT_UPDATE.method,
      headers: authHeaders(accessToken),
      body: JSON.stringify({ data: fields })
    }
  )
  await readJson(response)
}

export const deleteDocumentRequirement = async (
  accessToken: string | null,
  requirementId: string
): Promise<void> => {
  const response = await fetch(
    buildFullUrl(BACKEND_BASE_URL, API_ROUTES.CONFIG.DOCUMENT_REQUIREMENT_DELETE, { requirementId }),
    {
      method: API_ROUTES.CONFIG.DOCUMENT_REQUIREMENT_DELETE.method,
      headers: authHeaders(accessToken)
    }
  )
  await readJson(response)
}
